import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { PhoneApi, AssignNumberCommonStore } from 'entities/Phone'
import {
  type IParamsCreateNumbersLandline,
  type IParamsNumbersLandlineTeamCreate,
} from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { TextEnableLandlineStore } from 'pages/settings/pages/numbers/pages/TextEnableLandline/store/TextEnableLandlineStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'

export class AssignNumberStore {
  private readonly _assignNumberCommonStore: AssignNumberCommonStore
  private _inboxNumber = ''

  constructor(
    private _textEnableLandlineStore: TextEnableLandlineStore,
    private _entities: IEntitiesStore
  ) {
    this._assignNumberCommonStore = new AssignNumberCommonStore(
      {
        allowedTabs: ['existing', 'new'],
        nextClickHandler: this.nextClickHandler,
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  get assignNumberCommonStore() {
    return this._assignNumberCommonStore
  }

  get assignType() {
    return this._assignNumberCommonStore.assignType
  }

  get payload(): Pick<IParamsCreateNumbersLandline, 'inbox_number' | 'phone_to_replace'> {
    return {
      inbox_number: this._inboxNumber,
      phone_to_replace: this._inboxNumber,
    }
  }

  nextClickHandler = async () => {
    try {
      runInAction(() => {
        this._inboxNumber = ''
      })

      if (this.assignType === 'existing') {
        runInAction(() => {
          this._inboxNumber = this._assignNumberCommonStore.activeInbox?.number?.number || ''
        })

        const response = await PhoneApi.createNumbersLandlineInboxUpdate(
          this._textEnableLandlineStore.payload
        )
        const data = response.data

        if ('error' in data) {
          toastStore.add({
            id: nanoid(),
            type: 'error',
            title: data.error,
          })

          return
        }
      }

      if (this.assignType === 'new') {
        const newInboxStore = this._assignNumberCommonStore.newInboxStore

        const payload: IParamsNumbersLandlineTeamCreate = {
          name: newInboxStore.newInboxName.trim(),
          phone: this._textEnableLandlineStore.payload.phone,
          members: newInboxStore.selectedMembersIds.map((id) => Number(id)),
        }

        const response = await PhoneApi.createNumbersLandlineTeamCreate(payload)
        const data = response.data

        if ('error' in data) {
          toastStore.add({
            id: nanoid(),
            type: 'error',
            title: data.error,
          })

          return
        } else {
          const inbox = this._entities.inboxesStore.addItem(data)

          if (inbox?.type === 'inbox') {
            runInAction(() => {
              this._inboxNumber = inbox.origin.number?.number || ''
            })

            await this._entities.inboxesStore.handleUpdateTeamInbox(inbox.id)
            await PhoneApi.createNumbersLandlineInboxUpdate(this._textEnableLandlineStore.payload)
          }
        }
      }

      uiStore.changeRouteViaVue({
        path: `/${NumbersPath.root}`,
      })
    } catch (e) {
      logger.error(e)
    }
  }
}
